import {
    FEATURE_CREDIT_RULE,
    normalizeFeatureKey
} from '@/core/credit/types';
import {
    adaptCapabilityCatalogToFeaturePolicySnapshot,
    isCapabilityCatalogSnapshot
} from '@/core/api/adapters/analysisPolicyAdapter';

const FALLBACK_TIER_ID = 'free';
const DEFAULT_SUB_FEATURE_CODE = 'default';
const RUNTIME_POLICY_KEY = '__FEATURE_POLICY_CONFIG__';

function createTierRule({ available, credits, confirmRequired, defaultInRun, visible_fields }) {
    return {
        availability: available ? 'full' : 'unavailable',
        credits: available ? credits : null,
        visible_fields: visible_fields,
        confirmation_required: Boolean(confirmRequired),
        default_in_run: Boolean(defaultInRun)
    };
}

function createTierMatrix({ free, standard, pro }) {
    return {
        free: createTierRule(free),
        standard: createTierRule(standard),
        pro: createTierRule(pro)
    };
}

const DEFAULT_CAPABILITY_CATALOG_SNAPSHOT = Object.freeze({
    policy_version: 'fallback.2026-04-08.v2',
    run_code: 'upload_default_analysis',
    tiers: ['free', 'standard', 'pro'],
    tier_benefits: {},
    default_bundle_by_tier: {},
    capabilities: [
        {
            capability_code: 'upload.analysis_entry',
            module_code: 'upload',
            submodule_code: 'analysis_entry',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 5, confirmRequired: false, defaultInRun: true, visible_fields: '*' },
                standard: { available: true, credits: 5, confirmRequired: false, defaultInRun: true, visible_fields: '*' },
                pro: { available: true, credits: 5, confirmRequired: false, defaultInRun: true, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_task.default',
            module_code: 'result_task',
            submodule_code: 'default',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_guide.luminosity_histogram',
            module_code: 'result_guide',
            submodule_code: 'luminosity_histogram',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_guide.rgb_histogram',
            module_code: 'result_guide',
            submodule_code: 'rgb_histogram',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_guide.luminance_rgb_mask',
            module_code: 'result_guide',
            submodule_code: 'luminance_rgb_mask',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_guide.gaps_text_description',
            module_code: 'result_guide',
            submodule_code: 'gaps_text_description',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'summary.ai_gaps_summary',
            module_code: 'summary',
            submodule_code: 'ai_gaps_summary',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'summary.retouch_workflow',
            module_code: 'summary',
            submodule_code: 'retouch_workflow',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_guide.gaps_visual_panel',
            module_code: 'result_guide',
            submodule_code: 'gaps_visual_panel',
            tier_matrix: createTierMatrix({
                free: { available: false, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: false, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'compare.luminance_rgb_mask',
            module_code: 'compare',
            submodule_code: 'luminance_rgb_mask',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'compare.finetune_slider',
            module_code: 'compare',
            submodule_code: 'finetune_slider',
            tier_matrix: createTierMatrix({
                free: { available: false, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: false, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'compare.point_inspector',
            module_code: 'compare',
            submodule_code: 'point_inspector',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'result_task.gaps_card_overall',
            module_code: 'result_task',
            submodule_code: 'gaps_card_overall',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'atomic_capability.image_tab',
            module_code: 'atomic_capability',
            submodule_code: 'image_tab',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'atomic_capability.tonal_exposure',
            module_code: 'atomic_capability',
            submodule_code: 'tonal_exposure',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'atomic_capability.color_grading',
            module_code: 'atomic_capability',
            submodule_code: 'color_grading',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 2, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 2, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 2, confirmRequired: true, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'atomic_capability.global_color_base',
            module_code: 'atomic_capability',
            submodule_code: 'global_color_base',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 0, confirmRequired: false, defaultInRun: false, visible_fields: '*' }
            })
        },
        {
            capability_code: 'atomic_capability.global_color_palette',
            module_code: 'atomic_capability',
            submodule_code: 'global_color_palette',
            tier_matrix: createTierMatrix({
                free: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                standard: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' },
                pro: { available: true, credits: 1, confirmRequired: true, defaultInRun: false, visible_fields: '*' }
            })
        }
    ]
});

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function readRuntimePolicySnapshot() {
    if (typeof window === 'undefined' || !window[RUNTIME_POLICY_KEY]) {
        return null;
    }
    return window[RUNTIME_POLICY_KEY];
}

function toTrimmedString(value) {
    return String(value || '').trim();
}

function assertNonEmptyString(value, fieldName, context) {
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }
    throw new Error(`[featurePolicyConfig] Invalid ${fieldName}${context}`);
}

function sanitizeTierAvailability(value, context) {
    if (!value || typeof value !== 'object') {
        throw new Error(`[featurePolicyConfig] Invalid tierAvailability${context}`);
    }

    const entries = Object.entries(value)
        .filter(([tierId]) => typeof tierId === 'string' && tierId.trim())
        .map(([tierId, allowed]) => [tierId.trim(), Boolean(allowed)]);

    if (entries.length === 0) {
        throw new Error(`[featurePolicyConfig] Empty tierAvailability${context}`);
    }

    return Object.fromEntries(entries);
}

function sanitizeCreditCostByTier(value) {
    if (!value || typeof value !== 'object') {
        return {};
    }
    return Object.entries(value).reduce((acc, [tierId, cost]) => {
        if (typeof tierId !== 'string' || !tierId.trim()) {
            return acc;
        }
        const normalizedCost = Number(cost);
        if (!Number.isFinite(normalizedCost) || normalizedCost < 0) {
            throw new Error(`[featurePolicyConfig] Invalid credit cost for tier ${tierId}`);
        }
        acc[tierId.trim()] = normalizedCost;
        return acc;
    }, {});
}

function sanitizeTierRuleByTier(value, context) {
    if (!value || typeof value !== 'object') {
        return {};
    }

    return Object.entries(value).reduce((acc, [tierId, rule]) => {
        const normalizedTierId = toTrimmedString(tierId);
        if (!normalizedTierId) {
            return acc;
        }
        const normalizedRule = toTrimmedString(rule);
        if (!Object.values(FEATURE_CREDIT_RULE).includes(normalizedRule)) {
            throw new Error(`[featurePolicyConfig] Unsupported creditRuleByTier ${normalizedRule}${context}`);
        }
        acc[normalizedTierId] = normalizedRule;
        return acc;
    }, {});
}

function sanitizeBooleanByTier(value) {
    if (!value || typeof value !== 'object') {
        return {};
    }
    return Object.entries(value).reduce((acc, [tierId, flag]) => {
        const normalizedTierId = toTrimmedString(tierId);
        if (!normalizedTierId) {
            return acc;
        }
        acc[normalizedTierId] = Boolean(flag);
        return acc;
    }, {});
}

function sanitizeTierAvailabilityStateByTier(value, context) {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const ALLOWED_AVAILABILITY = new Set(['unavailable', 'partial', 'full']);
    return Object.entries(value).reduce((acc, [tierId, availability]) => {
        const normalizedTierId = toTrimmedString(tierId);
        if (!normalizedTierId) {
            return acc;
        }
        const normalizedAvailability = toTrimmedString(availability);
        if (!ALLOWED_AVAILABILITY.has(normalizedAvailability)) {
            throw new Error(`[featurePolicyConfig] Unsupported tierAvailabilityStateByTier value ${normalizedAvailability}${context}`);
        }
        acc[normalizedTierId] = normalizedAvailability;
        return acc;
    }, {});
}

function sanitizeVisibleFieldsByTier(value, context) {
    if (!value || typeof value !== 'object') {
        return {};
    }

    return Object.entries(value).reduce((acc, [tierId, visible_fields]) => {
        const normalizedTierId = toTrimmedString(tierId);
        if (!normalizedTierId) {
            return acc;
        }
        if (visible_fields === '*') {
            acc[normalizedTierId] = '*';
            return acc;
        }
        if (!Array.isArray(visible_fields)) {
            throw new Error(`[featurePolicyConfig] Invalid visibleFieldsByTier${context}`);
        }
        acc[normalizedTierId] = visible_fields
            .map((field) => toTrimmedString(field))
            .filter(Boolean);
        return acc;
    }, {});
}

function sanitizeFeaturePolicyItem(item, index) {
    if (!item || typeof item !== 'object') {
        throw new Error(`[featurePolicyConfig] Invalid feature item at index ${index}`);
    }

    const context = ` at feature[${index}]`;
    const featureCode = assertNonEmptyString(item.featureCode, 'featureCode', context);
    const subFeatureCode = typeof item.subFeatureCode === 'string' && item.subFeatureCode.trim()
        ? item.subFeatureCode.trim()
        : DEFAULT_SUB_FEATURE_CODE;

    const hasLegacyCreditRule = typeof item.creditRule === 'string' && item.creditRule.trim();
    const hasTierCreditRule = item.creditRuleByTier && typeof item.creditRuleByTier === 'object';

    if (!hasLegacyCreditRule && !hasTierCreditRule) {
        throw new Error(`[featurePolicyConfig] Missing credit rule config${context}`);
    }

    let creditRule;
    let confirmRequired = false;

    if (hasLegacyCreditRule) {
        creditRule = assertNonEmptyString(item.creditRule, 'creditRule', context);
        if (!Object.values(FEATURE_CREDIT_RULE).includes(creditRule)) {
            throw new Error(`[featurePolicyConfig] Unsupported creditRule ${creditRule}${context}`);
        }
        confirmRequired = Boolean(item.confirmRequired);
        if (creditRule === FEATURE_CREDIT_RULE.CONFIRM && !confirmRequired) {
            throw new Error(`[featurePolicyConfig] confirm creditRule must set confirmRequired=true${context}`);
        }
        if (creditRule !== FEATURE_CREDIT_RULE.CONFIRM && confirmRequired) {
            throw new Error(`[featurePolicyConfig] only confirm creditRule can set confirmRequired=true${context}`);
        }
    }

    return {
        featureCode,
        subFeatureCode,
        tierAvailability: sanitizeTierAvailability(item.tierAvailability, context),
        ...(hasLegacyCreditRule ? { creditRule, confirmRequired } : {}),
        purchasable: item.purchasable !== false,
        creditCostByTier: sanitizeCreditCostByTier(item.creditCostByTier),
        creditRuleByTier: sanitizeTierRuleByTier(item.creditRuleByTier, context),
        confirmRequiredByTier: sanitizeBooleanByTier(item.confirmRequiredByTier),
        tierAvailabilityStateByTier: sanitizeTierAvailabilityStateByTier(item.tierAvailabilityStateByTier, context),
        visibleFieldsByTier: sanitizeVisibleFieldsByTier(item.visibleFieldsByTier, context),
        defaultInRunByTier: sanitizeBooleanByTier(item.defaultInRunByTier),
        source: toTrimmedString(item.source) || 'unknown'
    };
}

function adaptSnapshotFromCatalog(snapshot) {
    return adaptCapabilityCatalogToFeaturePolicySnapshot(snapshot, {
        includeLocalFallback: true
    });
}

function resolvePolicySnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return null;
    }

    if (isCapabilityCatalogSnapshot(snapshot)) {
        try {
            return adaptSnapshotFromCatalog(snapshot);
        } catch (error) {
            console.warn('[featurePolicyConfig] failed to adapt capability catalog snapshot, fallback to default snapshot', error);
            return null;
        }
    }

    if (typeof snapshot.version === 'string' && Array.isArray(snapshot.features)) {
        return snapshot;
    }

    return null;
}

export function validateFeaturePolicySnapshot(snapshot, options = {}) {
    const { allowEmptyFeatures = false } = options;

    if (!snapshot || typeof snapshot !== 'object') {
        throw new Error('[featurePolicyConfig] Snapshot must be an object');
    }

    const version = assertNonEmptyString(snapshot.version, 'version', ' on snapshot');
    const features = Array.isArray(snapshot.features) ? snapshot.features : null;
    if (!features) {
        throw new Error('[featurePolicyConfig] Snapshot.features must be an array');
    }
    if (!allowEmptyFeatures && features.length === 0) {
        throw new Error('[featurePolicyConfig] Snapshot.features cannot be empty');
    }

    const normalizedFeatures = features.map((item, index) => sanitizeFeaturePolicyItem(item, index));
    const dedupeMap = new Set();

    normalizedFeatures.forEach((item) => {
        const key = normalizeFeatureKey(item.featureCode, item.subFeatureCode);
        if (dedupeMap.has(key)) {
            throw new Error(`[featurePolicyConfig] Duplicate feature policy key: ${key}`);
        }
        dedupeMap.add(key);
    });

    return {
        version,
        features: normalizedFeatures
    };
}

export function getDefaultFeaturePolicySnapshot() {
    const runtimeSnapshot = readRuntimePolicySnapshot();
    const resolvedRuntimeSnapshot = resolvePolicySnapshot(runtimeSnapshot);
    const sourceSnapshot = resolvedRuntimeSnapshot || resolvePolicySnapshot(DEFAULT_CAPABILITY_CATALOG_SNAPSHOT);
    const validated = validateFeaturePolicySnapshot(sourceSnapshot);
    return clone(validated);
}

export function getFeaturePolicy(snapshot, featureCode, subFeatureCode = DEFAULT_SUB_FEATURE_CODE) {
    const resolvedSnapshot = resolvePolicySnapshot(snapshot);
    const validated = validateFeaturePolicySnapshot(resolvedSnapshot || getDefaultFeaturePolicySnapshot());
    const key = normalizeFeatureKey(featureCode, subFeatureCode);
    const matched = validated.features.find((item) => normalizeFeatureKey(item.featureCode, item.subFeatureCode) === key);
    if (matched) {
        return clone(matched);
    }

    return {
        featureCode,
        subFeatureCode,
        tierAvailability: { [FALLBACK_TIER_ID]: true },
        creditRule: FEATURE_CREDIT_RULE.NONE,
        confirmRequired: false,
        purchasable: true,
        creditCostByTier: {}
    };
}

export function listFeaturePolicies(snapshot) {
    const resolvedSnapshot = resolvePolicySnapshot(snapshot);
    return validateFeaturePolicySnapshot(resolvedSnapshot || getDefaultFeaturePolicySnapshot()).features.map(clone);
}

const DEFAULT_FEATURE_POLICY_SNAPSHOT = validateFeaturePolicySnapshot(
    adaptCapabilityCatalogToFeaturePolicySnapshot(DEFAULT_CAPABILITY_CATALOG_SNAPSHOT, {
        includeLocalFallback: true
    })
);

validateFeaturePolicySnapshot(DEFAULT_FEATURE_POLICY_SNAPSHOT);

export {
    DEFAULT_CAPABILITY_CATALOG_SNAPSHOT,
    DEFAULT_FEATURE_POLICY_SNAPSHOT,
    DEFAULT_SUB_FEATURE_CODE,
    FALLBACK_TIER_ID
};
